import { lazy, Suspense } from 'react'
import { Button } from './components'
import { auth } from './lib/api'
import { ROUTES, routeHref, useRoute, type Route } from './lib/router'

/**
 * App shell: neutral chrome (DESIGN.md) + a hash router. A left sidebar on
 * desktop and a horizontal tab bar on mobile link the five views; the active
 * feature page is lazy-loaded into the content area. Light and dark are equally
 * first-class via the CSS-variable tokens — no forced theme.
 */

// Lazy-import the exact page files feature agents own. Code-splitting keeps the
// initial bundle small and isolates each feature.
const PAGES: Record<Route, ReturnType<typeof lazy>> = {
  inbox: lazy(() => import('./features/inbox/InboxPage')),
  concepts: lazy(() => import('./features/concepts/ConceptsPage')),
  poses: lazy(() => import('./features/poses/PosesPage')),
  references: lazy(() => import('./features/references/ReferencesPage')),
  shoots: lazy(() => import('./features/shoots/ShootsPage')),
  lighting: lazy(() => import('./features/lighting/LightingPage')),
  locations: lazy(() => import('./features/locations/LocationsPage')),
  people: lazy(() => import('./features/people/PeoplePage')),
  search: lazy(() => import('./features/search/SearchPage')),
}

// Lighting is a primary, daily-driver use of the app, so it leads the nav.
const NAV: ReadonlyArray<{ id: Route; label: string }> = [
  { id: 'lighting', label: 'Lighting' },
  { id: 'references', label: 'References' },
  { id: 'concepts', label: 'Concepts' },
  { id: 'poses', label: 'Poses' },
  { id: 'shoots', label: 'Shoots' },
  { id: 'locations', label: 'Locations' },
  { id: 'people', label: 'People' },
  { id: 'inbox', label: 'Inbox' },
  { id: 'search', label: 'Search' },
]

function NavLink({ id, label, active }: { id: Route; label: string; active: boolean }) {
  return (
    <a
      href={routeHref(id)}
      aria-current={active ? 'page' : undefined}
      className={
        'flex min-h-[44px] items-center rounded-md px-md text-body transition-colors duration-150 ' +
        (active
          ? 'bg-accent/10 font-medium text-accent'
          : 'text-secondary hover:bg-foreground/5 hover:text-foreground')
      }
    >
      {label}
    </a>
  )
}

export function App() {
  const route = useRoute()

  const handleSignOut = () => {
    void auth.logout().then(() => window.location.reload())
  }

  const ActivePage = PAGES[route]

  return (
    <div className="min-h-dvh bg-background text-foreground">
      {/* Header — neutral chrome, quiet (DESIGN.md). */}
      <header className="sticky top-0 z-10 border-b border-border bg-surface">
        <div className="flex items-center justify-between gap-md px-md py-md">
          <h1 className="text-h1 font-display text-foreground">Photoshoot</h1>
          <Button variant="secondary" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>

        {/* Mobile: horizontal scrollable tab bar (hidden on md+). */}
        <nav
          aria-label="Sections"
          className="flex gap-sm overflow-x-auto px-md pb-sm md:hidden"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.id}
              id={item.id}
              label={item.label}
              active={route === item.id}
            />
          ))}
        </nav>
      </header>

      <div className="mx-auto flex w-full max-w-[1200px]">
        {/* Desktop: fixed-width sidebar (hidden on mobile). */}
        <aside className="hidden w-56 shrink-0 border-r border-border md:block">
          <nav aria-label="Sections" className="flex flex-col gap-xs p-md">
            {NAV.map((item) => (
              <NavLink
                key={item.id}
                id={item.id}
                label={item.label}
                active={route === item.id}
              />
            ))}
          </nav>
        </aside>

        {/* Active feature page. */}
        <main className="min-w-0 flex-1">
          <Suspense
            fallback={
              <p className="px-md py-lg text-body text-secondary" role="status">
                Loading…
              </p>
            }
          >
            <ActivePage />
          </Suspense>
        </main>
      </div>
    </div>
  )
}
